// src/components/originator-pending.js

import React, { useState, useEffect, Fragment } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { RestHelper } from '../../helper/restHelper';
import { AdminEditCreatedRequest, AdminEditSelectChoice, AdminAssignDirect, AdminApptNotesHandler, AdminUploadStatus, AdminEditStatus } from "../../components/admin";
import AdminPickNewNotary from './admin-pick-new-notary';
import { PopupOverlay, GeneralUploadStatus, ProfilePopupOverlay, GeneralGroupFileUpload } from '../../components';

// Build the bid's "Omaha, NE • ~8 mi" line. Returns null when nothing to show.
// estDistance is hidden when null/missing; only rendered (incl. "~0 mi") when it's literally a number.
function formatBidLocation(agent) {
  const place = [agent.estCity, agent.estState].filter(Boolean).join(', ');
  const dist = typeof agent.estDistance === 'number' ? `~${agent.estDistance} mi` : '';
  return [place, dist].filter(Boolean).join(' • ') || null;
}

// Collapsible Agent Card Component
const AgentCard = ({ agent, formatPhoneNumber, getProfile, updateRequest, apptRequestId, apptFirm, apptFilled, apptCity, apptState }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const bidLocation = formatBidLocation(agent);

  // Determine status color
  const getStatusColor = (status) => {
    if (status === 'Accepted' || status === 'Accept') return 'text-green-600 bg-green-100';
    if (status === 'Declined' || status === 'Decline') return 'text-red-600 bg-red-100';
    if (status === 'Pending') return 'text-amber-600 bg-amber-100';
    return 'text-neutral-600 bg-neutral-100';
  };

  // Determine experience indicator class
  const getExperienceClass = () => {
    const completed = agent.notaryCompleted || 0;
    const scheduled = agent.notaryScheduled || 0;
    if (completed > 3) return 'agent-experience-high';
    if (completed > 0 || scheduled > 0) return 'agent-experience-some';
    return 'agent-experience-none';
  };

  // Generate Snapdocs search URL
  const getSnapdocsSearchUrl = () => {
    const query = `${agent.apptNotary} notary snapdocs`;
    return `https://www.google.com/search?q=${encodeURIComponent(query)}`;
  };

  return (
    <div className="bg-white rounded-lg border border-neutral-200 mb-3 overflow-hidden">
      {/* Header - Always visible */}
      <button
        className={`w-full px-4 py-3 flex items-center justify-between transition-colors ${getExperienceClass()}`}
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center gap-4">
          <span className="flex flex-col text-left">
            <span className="font-medium text-neutral-800">{agent.apptNotary}</span>
            {bidLocation && <span className="text-xs text-neutral-500 font-normal">{bidLocation}</span>}
          </span>
          <a
            href={getSnapdocsSearchUrl()}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            className="text-neutral-400 hover:text-blue-600 transition-colors"
            title="Search Snapdocs"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </a>
          <span className="text-blue-600 font-semibold">${agent.apptNotaryBid}</span>
          <span className={`text-xs px-2 py-1 rounded-full ${getStatusColor(agent.apptNotaryApproval)}`}>
            {agent.apptNotaryApproval}
          </span>
          {agent.affiliatePartner && (
            <span className="text-xs px-2 py-1 rounded-full bg-purple-100 text-purple-800 font-medium">Affiliate Partner</span>
          )}
        </div>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className={`h-5 w-5 text-neutral-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {/* Expanded Content */}
      {isExpanded && (
        <div className="px-4 pb-4 border-t border-neutral-100">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
            <div>
              <div className="text-xs text-neutral-500 uppercase tracking-wide mb-1">Phone</div>
              <div className="text-neutral-800">{formatPhoneNumber(agent.notaryPhone)}</div>
            </div>
            <div>
              <div className="text-xs text-neutral-500 uppercase tracking-wide mb-1">Email</div>
              <div className="text-neutral-800">{agent.notaryEmail}</div>
            </div>
          </div>
          <div className="mt-3">
            <button
              className="text-blue-600 hover:text-blue-800 text-sm font-medium hover:underline"
              onClick={() => getProfile(agent)}
            >
              View Full Profile
            </button>
          </div>
          {!apptFilled && (
            <div className="flex gap-3 mt-4 pt-3 border-t border-neutral-100">
              <button
                className="bg-green-600 hover:bg-green-700 text-white text-sm font-medium py-2 px-4 rounded-lg transition-colors"
                onClick={() => updateRequest(apptRequestId, agent.notaryEmail, 'Accept', apptFirm)}
              >
                Accept
              </button>
              <button
                className="bg-neutral-200 hover:bg-neutral-300 text-neutral-700 text-sm font-medium py-2 px-4 rounded-lg transition-colors"
                onClick={() => updateRequest(apptRequestId, agent.notaryEmail, 'Decline', apptFirm)}
              >
                Decline
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const AdminViewRequest = (data) => {

  const [requestDetails, setRequestDetails] = useState([])
  const [isLoaded, setIsLoaded] = useState(false)
  const [showDetails, setShowDetails] = useState(true)
  const [showResponse, setShowResponse] = useState(false)
  const [updateResponse, setUpdateResponse] = useState([])
  const [uploadInfo, setUploadInfo] = useState({})
  const [uploadResponse, setUploadResponse] = useState()
  const [file, setFile] = useState()
  const [fileLoaded, setFileLoaded] = useState(false)
  const [showFiles, setShowFiles] = useState(false)
  const [showFileName, setShowFileName] = useState(true)
  const [isMounted, setIsMounted] = useState(false)
  const [isPending, setIsPending] = useState(false)
  const [isScheduled, setIsScheduled] = useState(false)
  const [editScreen, setEditScreen] = useState(false)
  const [editNotary, setEditNotary] = useState(false)
  const [editStatus, setEditStatus] = useState(false)
  const [assignDirect, setAssignDirect] = useState(false)
  const [editDetails, setEditDetails] = useState(false)
  const [requestId, setRequestId] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [showPopup, setShowPopup] = useState(false)
  const [showNotaryPopup, setShowNotaryPopup] = useState(false)
  const [notaryPaid, setNotaryPaid] = useState(false)
  const [profileEmail, setProfileEmail] = useState('')
  const [passedMessage, setPassedMessage] = useState('')
  const [passedObject, setPassedObject] = useState('')
  const [isChecked, setIsChecked] = useState(false)
  const [isApproved, setIsApproved] = useState(false)
  const [pendingActionType, setPendingActionType] = useState('')
  const [resendStatus, setResendStatus] = useState(null)

  const api = RestHelper()

  const dropZoneStyle2 = {
    border: '2px solid #666',       // Medium gray border
    borderRadius: '5px',
    padding: '5px',
    textAlign: 'left',
    color: '#333',                  // Darker text color for contrast
    cursor: 'arrow',
    //backgroundColor: '#ddd',        // Medium gray background
  };

  useEffect(() => {
    if (fileLoaded) {
      uploadHandler()
    }
  }, [file, setFile])


useEffect(() => {
  window.scrollTo(0, 0);
}, []);

 const setUploadComplete = (event) => {
  var checkStatus = (event)
  setIsChecked(checkStatus)
  const reqData = { 'action' : 'markFilesUploadedStatus', 'request_id' : data.requestId, 'fileStatus' : checkStatus}
  const options = {}
  api
    .postCreate('/manage-appts-admin', options, reqData)
    .then(res => doNothing())
    .catch(err => console.log(err))
 }

 const setUploadsApproved = (event) => {
  var checkStatus = (event)
  setIsApproved(checkStatus)
  const reqData = { 'action' : 'markUploadsApprovedStatus', 'request_id' : data.requestId, 'approvedStatus' : checkStatus}
  const options = {}
  api
    .postCreate('/manage-appts-admin', options, reqData)
    .then(res => doNothing())
    .catch(err => console.log(err))
 }

  function doNothing(){
  
 }



  const fileChangedHandler = event => {
    setFileLoaded(true)
    setFile(event.target.files[0])
    setShowFileName(false)
  }
  const uploadHandler = () => {
    const options = {}
    setIsLoading(true)
    setFileLoaded(false)
    const fileData = { 'unique_id': data.requestId, 'file_name': file.name }
    api
      .postCreate('/upload-file-admin', options, fileData)
      .then(res => setUploadInfo(res))
      .catch(err => console.log(err))
  }

  const getInvoice = (e) => {
    const options = {}
    const fileData = { 'unique_id': data.requestId, 'file_name': e }
    api
      .postCreate('/get-invoice-file', options, fileData)
      .then(res => window.open(res.get_url, "_blank"))
      .catch(err => console.log(err))
  }

  function handleMessage(value) {
    if (value === 'Details') {
      setEditScreen(false)
      setEditDetails(true)
    }
    if (value === 'Notary') {
      console.log((requestDetails.assigned_notary))
      setEditScreen(false)
      setEditNotary(true)
    }
    if (value === 'Status') {
      setEditScreen(false)
      setEditStatus(true)
    }
    if (value === 'DirectAssign') {
      setEditScreen(false)
      setAssignDirect(true)
    }
  }

  const scanBackCheck = (checkFlag) => {
    var scanBackValue = ''
    if (checkFlag === 'true') {
      scanBackValue = 'SCAN BACKS ARE REQUIRED'
    }
    else {
      scanBackValue = 'NONE'
    }
    return scanBackValue
  }

  function setTransactionType(setValue) {
    if (setValue === '0') {
      return 'Not Defined'
    }
    else if (setValue === '1') {
      return 'Purchase (Buyer Side)'
    }
    else if (setValue === '2') {
      return 'Refinance'
    }
    else if (setValue === '3') {
      return 'Seller Side Closing'
    }
    else if (setValue === '4') {
      return 'Other'
    }
    else {
      return 'Not Defined'
    }
  }

  function setFinanceType(setValue) {
    if (setValue === '10') {
      return 'Not Defined'
    }
    else if (setValue === '11') {
      return 'FHA'
    }
    else if (setValue === '12') {
      return 'Conventional'
    }
    else if (setValue === '13') {
      return 'Reverse'
    }
    else if (setValue === '14') {
      return 'HELOC'
    }
    else if (setValue === '15') {
      return 'Commercial'
    }
    else if (setValue === '16') {
      return 'VA'
    }
    else {
      return 'Not Defined'
    }
  }

  function updateNotaryPaidStatus(){
    const reqData = { 'action': 'markNotaryPaid', 'request_id': data.requestId}
    const options = {}
    api
      .postCreate('/manage-appts-admin', options, reqData)
      .then(res => setNotaryPaid(true))
      .catch(err => console.log(err))
  }

  function formatPhoneNumber(phoneNumber) {
    // Remove any non-digit characters from the phone number
    var cleaned = phoneNumber.replace(/\D/g, '');
    var len = cleaned.length
    if (len > 10) {
      var temp = cleaned.substring(0, 10)
      cleaned = temp
    }
    if (len < 10) {
      while (cleaned.length < 10) {
        cleaned += 9
      }
    }

    // Format the cleaned phone number as (123) 456-7890
    const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
    return '(' + match[1] + ') ' + match[2] + '-' + match[3];
  }

  useEffect(() => {
    if (isMounted) {
      const formData = new FormData()
      const key = uploadInfo.fields['key']
      const keyId = uploadInfo.fields['AWSAccessKeyId']
      const policy = uploadInfo.fields['policy']
      const token = uploadInfo.fields['x-amz-security-token']
      const signature = uploadInfo.fields['signature']
      formData.append('key', key)
      formData.append('AWSAccessKeyId', keyId)
      formData.append('policy', policy)
      formData.append('x-amz-security-token', token)
      formData.append('signature', signature)
      formData.append('file', file)
      api
        .fileCreate(uploadInfo.url, formData)
        .then(res => setUploadResponse(res))
        .catch(err => console.log(err))
      setShowFileName(true)
      setShowFiles(false)
      sleep(7000).then(() => { clearResponseMessage() });
    } else {
      setIsMounted(true)
    }
  }, [uploadInfo, setUploadInfo])

  const reqData = { data }
  const options = {}
  if (!isLoaded) {
    api
      .postCreate('/get-request-details', options, reqData)
      .then(res => initialLoad(res))
      .catch(err => console.log(err))
    setIsLoaded(true)
  }

  function sleep(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }
  const dateFormat = (inputDate) => {
    const dateArray = inputDate.split("-")
    const date = dateArray[1] + '/' + dateArray[2] + '/' + dateArray[0]
    return date
  }
  const initialLoad = (res) => {
    setRequestDetails(res)
    setIsChecked(res[0]['fileUploadStatus'])
    setIsApproved(res[0]['uploadsApproved'])
    setNotaryPaid(res[0].notaryPaid)
    if (res[0].apptFilled) {
      setIsScheduled(true)
    }
    if (!res[0].apptFilled) {
      setIsPending(true)
    }
  }
  const getTempUrl = (e) => {
    const options = {}
    const fileData = { 'unique_id': data.requestId, 'file_name': e }
    api
      .postCreate('/get-stored-file', options, fileData)
      /*.then(res => window.open(res.get_url, "_blank"))*/
      .then(res => window.location.href = res.get_url)
      .catch(err => console.log(err))
  }


  const getTempUrlCurrentFile = () => {
    const options = {}
    const fileData = { 'unique_id': data.requestId, 'file_name': file.name }
    api
      .postCreate('/get-stored-file', options, fileData)
      .then(res => window.open(res.get_url, "_blank"))
      .catch(err => console.log(err))
  }

  function convertToSimpleDateMessage(updatedTime) {
    const date = new Date(updatedTime * 1000)
    const dateString = date.toLocaleString('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    })
    return dateString
  }

  function clearResponseMessage() {
    setFile('')
    setIsLoading(false)
    setShowDetails(true)
    setShowResponse(false)
    const reqData = { data }
    const options = {}
    api
      .postCreate('/get-request-details', options, reqData)
      .then(res => initialLoad(res))
      .catch(err => console.log(err))
  }

  function showEditView(request) {
    console.log({ requestDetails })
    setShowDetails(false)
    setRequestId(request)
    setEditScreen(true)
  }

  function clearModal() {
    setShowPopup(false)
    setShowNotaryPopup(false)
  }
  function callDeleteFileService(inputData) {
    const reqData = { 'action': 'deleteFile', 'request_id': data.requestId, 'fileName': inputData }
    const options = {}
    api
      .postCreate('/manage-appts-admin', options, reqData)
      .then(res => clearResponseMessage())
      .catch(err => console.log(err))
  }

  function deleteFile(e) {
    //callDeleteFileService(e)
    var messageToSend = 'Are you sure you wish to delete ' + e + '?'
    setPassedMessage(messageToSend)
    setPassedObject(e)
    setShowPopup(true)
    const btnRef = document.getElementById(e)
    btnRef.blur()
    console.log(e)
  }

  function getProfile(e) {
    setProfileEmail(e)
    setShowNotaryPopup(true)
  }

  function confirmAction(e) {
    if (pendingActionType === 'resend') {
      callResendConfirmation()
    } else {
      callDeleteFileService(e)
    }
  }

  function openResendModal(notaryEmail) {
    setPassedMessage('Resend the confirmation email to ' + notaryEmail + '?')
    setPassedObject(data.requestId)
    setPendingActionType('resend')
    setShowPopup(true)
  }

  function callResendConfirmation() {
    const reqData = { 'action': 'resendNotaryConfirmation', 'request_id': data.requestId }
    const options = {}
    api
      .postCreate('/manage-appts-admin', options, reqData)
      .then(res => {
        setResendStatus('success')
        sleep(4000).then(() => setResendStatus(null))
      })
      .catch(err => setResendStatus('error'))
  }

  function setAction(passedAction) {
    if (passedAction === 'write') {
      return 'Uploaded'
    }
    else {
      return 'Downloaded'
    }
  }
  const updateRequest = (reqId, reqNotary, Status, firm) => {
    const options = {}
    const params = { 'apptReqId': reqId, 'apptNotary': reqNotary, 'reqStatus': Status, 'apptFirm': firm }
    api
      .postCreate('/accept-notary-offer', options, params)
      .then(res => setUpdateResponse(res))
      .catch(err => console.log(err))
    setShowDetails(false)
    setShowResponse(true)

  }

  const updateRequestCompleted = (reqId, reqNotary, Status, firm) => {
    const options = {}
    const params = { 'request_id': reqId, 'apptNotary': reqNotary, 'reqStatus': Status, 'action': 'setComplete', 'apptFirm': firm }
    api
      .postCreate('/manage-appts-admin', options, params)
      .then(res => setUpdateResponse(res))
      .catch(err => console.log(err))
    setShowDetails(false)
    setShowResponse(true)

  }

  const sendInvoice = (reqId) => {
    const options = {}
    const params = { 'request_id': reqId, 'action': 'sendInvoice', }
    api
      .postCreate('/manage-appts-admin', options, params)
      .then(res => setUpdateResponse(res))
      .catch(err => console.log(err))
    setShowDetails(false)
    setShowResponse(true)

  }

  return (
    <div className="w-full max-w-4xl mx-auto py-4">
      {showDetails && (<div>
        {requestDetails.map((items =>
          <div key={items.appt_requestId}>
            {/* Header */}
            <div className="mb-6">
              <h1 className="text-2xl font-bold text-neutral-800">Request {items.appt_requestId}</h1>
              <div className="w-12 h-1 bg-blue-500 rounded mt-2"></div>
            </div>

            {/* Appointment Details Card */}
            <div className="bg-white rounded-xl shadow-md border border-neutral-200 mb-4 overflow-hidden">
              <div className="px-3 sm:px-5 py-3 border-b border-neutral-200">
                <h2 className="text-lg font-semibold text-neutral-800">Appointment Details</h2>
                <div className="w-12 h-1 bg-blue-500 rounded mt-2"></div>
              </div>
              <div className="p-3 sm:p-5 space-y-3">
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-1 sm:gap-4 py-2 border-b border-neutral-100">
                  <div className="font-medium text-neutral-600">Appointment Name</div>
                  <div className="sm:col-span-2 text-neutral-800">{items.apptName}</div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-1 sm:gap-4 py-2 border-b border-neutral-100">
                  <div className="font-medium text-neutral-600">When</div>
                  <div className="sm:col-span-2 text-neutral-800">{dateFormat(items.apptDate)} at {items.apptTime} {items.appt_timeZone}</div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-1 sm:gap-4 py-2 border-b border-neutral-100">
                  <div className="font-medium text-neutral-600">Where</div>
                  <div className="sm:col-span-2 text-neutral-800">{items.apptStreet}, {items.apptCity}, {items.apptState} {items.apptZip}</div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-1 sm:gap-4 py-2 border-b border-neutral-100">
                  <div className="font-medium text-neutral-600">File Number</div>
                  <div className="sm:col-span-2 text-neutral-800">{items.fileNumber}</div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-1 sm:gap-4 py-2 border-b border-neutral-100">
                  <div className="font-medium text-neutral-600">Lender</div>
                  <div className="sm:col-span-2 text-neutral-800">{items.lender}</div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-1 sm:gap-4 py-2 border-b border-neutral-100">
                  <div className="font-medium text-neutral-600">Scan Backs</div>
                  <div className="sm:col-span-2 text-neutral-800">{scanBackCheck(items.scanBack)}</div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-1 sm:gap-4 py-2 border-b border-neutral-100">
                  <div className="font-medium text-neutral-600">Transaction Type</div>
                  <div className="sm:col-span-2 text-neutral-800">{setTransactionType(items.transactionType)}</div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-1 sm:gap-4 py-2 border-b border-neutral-100">
                  <div className="font-medium text-neutral-600">Financing Type</div>
                  <div className="sm:col-span-2 text-neutral-800">{setFinanceType(items.financeType)}</div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-1 sm:gap-4 py-2">
                  <div className="font-medium text-neutral-600">Extra Notes</div>
                  <div className="sm:col-span-2 text-neutral-800">{items.apptMessages || '—'}</div>
                </div>
              </div>
            </div>

            {/* Contacts Card */}
            <div className="bg-white rounded-xl shadow-md border border-neutral-200 mb-4 overflow-hidden">
              <div className="px-3 sm:px-5 py-3 border-b border-neutral-200">
                <h2 className="text-lg font-semibold text-neutral-800">Contacts</h2>
                <div className="w-12 h-1 bg-blue-500 rounded mt-2"></div>
              </div>
              <div className="p-3 sm:p-5 space-y-3">
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-1 sm:gap-4 py-2 border-b border-neutral-100">
                  <div className="font-medium text-neutral-600 flex items-center gap-2">
                    Assigned Notary
                    {notaryPaid
                      ? <img src={'/img/us-dollar-paid.png'} title={convertToSimpleDateMessage(items.notaryPaidTime)} width='20' height='20' alt="Paid"/>
                      : <button onClick={updateNotaryPaidStatus} className="p-0 border-0 bg-transparent cursor-pointer focus:outline-none">
                          <img src={'/img/us-dollar-unpaid.png'} title={'Notary not paid'} width='20' height='20' alt="Unpaid"/>
                        </button>
                    }
                  </div>
                  <div className="sm:col-span-2 text-neutral-800">
                    <div>{items.assigned_notary_name}</div>
                    {items.assigned_notary && <div className="text-sm">{items.assigned_notary}</div>}
                    {items.assigned_notary_phone && <div className="text-sm">{formatPhoneNumber(items.assigned_notary_phone)}</div>}
                    {items.assigned_notary && (
                      <div className="mt-2">
                        <button
                          className="text-sm bg-neutral-100 hover:bg-neutral-200 text-neutral-700 font-medium py-1 px-3 rounded-lg transition-colors border border-neutral-300"
                          type="button"
                          onClick={() => openResendModal(items.assigned_notary)}
                        >
                          Resend Confirmation
                        </button>
                        {resendStatus === 'success' && (
                          <span className="ml-3 text-sm text-green-600 font-medium">Confirmation sent.</span>
                        )}
                        {resendStatus === 'error' && (
                          <span className="ml-3 text-sm text-red-600 font-medium">Failed to send. Please try again.</span>
                        )}
                      </div>
                    )}
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-1 sm:gap-4 py-2 border-b border-neutral-100">
                  <div className="font-medium text-neutral-600">Escrow Contact</div>
                  <div className="sm:col-span-2 text-neutral-800">
                    <div>{items.escrowName}</div>
                    <div className="text-sm">{items.escrowEmail}</div>
                    {items.escrowPhone && <div className="text-sm">{formatPhoneNumber(items.escrowPhone)}</div>}
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-1 sm:gap-4 py-2 border-b border-neutral-100">
                  <div className="font-medium text-neutral-600">Signer Name</div>
                  <div className="sm:col-span-2 text-neutral-800">{items.apptContactName}</div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-1 sm:gap-4 py-2 border-b border-neutral-100">
                  <div className="font-medium text-neutral-600">Signer Primary Phone</div>
                  <div className="sm:col-span-2 text-neutral-800">{items.apptContactPhone1}</div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-1 sm:gap-4 py-2 border-b border-neutral-100">
                  <div className="font-medium text-neutral-600">Signer Secondary Phone</div>
                  <div className="sm:col-span-2 text-neutral-800">{items.apptContactPhone2 || '—'}</div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-1 sm:gap-4 py-2 border-b border-neutral-100">
                  <div className="font-medium text-neutral-600">Signer Primary Email</div>
                  <div className="sm:col-span-2 text-neutral-800 break-all">{items.apptContactEmail1}</div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-1 sm:gap-4 py-2">
                  <div className="font-medium text-neutral-600">Signer Secondary Email</div>
                  <div className="sm:col-span-2 text-neutral-800 break-all">{items.apptContactEmail2 || '—'}</div>
                </div>
              </div>
            </div>

            {/* Invoice Card */}
            {items.invoiceFileName && (
              <div className="bg-white rounded-xl shadow-md border border-neutral-200 mb-4 overflow-hidden">
                <div className="px-3 sm:px-5 py-3 border-b border-neutral-200">
                  <h2 className="text-lg font-semibold text-neutral-800">Invoice</h2>
                  <div className="w-12 h-1 bg-blue-500 rounded mt-2"></div>
                </div>
                <div className="p-3 sm:p-5">
                  <button
                    className="text-blue-600 hover:text-blue-800 hover:underline font-medium"
                    title="Invoice File"
                    type="button"
                    onClick={() => getInvoice(items.invoiceFileName)}
                  >
                    {items.invoiceFileName}
                  </button>
                </div>
              </div>
            )}

            {/* File Management Card */}
            <div className="bg-white rounded-xl shadow-md border border-neutral-200 mb-4 overflow-hidden">
              <div className="px-3 sm:px-5 py-3 border-b border-neutral-200">
                <h2 className="text-lg font-semibold text-neutral-800">File Management</h2>
                <div className="w-12 h-1 bg-blue-500 rounded mt-2"></div>
              </div>
              <div className="p-3 sm:p-5">
                <GeneralGroupFileUpload requestId={data.requestId} clearResponseMessage={clearResponseMessage}/>

                {/* Upload Status Toggles */}
                <div className="space-y-3 mb-6">
                  <div className="flex items-center gap-3 p-4 bg-neutral-50 rounded-lg border border-neutral-200">
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input checked={isChecked} type="checkbox" onChange={(e) => setUploadComplete(e.target.checked)} className="sr-only peer"/>
                      <div className="w-11 h-6 bg-neutral-300 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-neutral-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                    </label>
                    <span className="text-neutral-700 text-sm sm:text-base">Mark all files as uploaded</span>
                  </div>
                  <div className="flex items-center gap-3 p-4 bg-neutral-50 rounded-lg border border-neutral-200">
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input checked={isApproved} type="checkbox" onChange={(e) => setUploadsApproved(e.target.checked)} className="sr-only peer"/>
                      <div className="w-11 h-6 bg-neutral-300 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-neutral-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                    </label>
                    <span className="text-neutral-700 text-sm sm:text-base">Uploaded docs reviewed and approved for notary drop</span>
                  </div>
                </div>

                {/* File Attachments */}
                <h3 className="text-base font-semibold text-neutral-800 mb-3">File Attachments</h3>
                <div className="space-y-3">
                  {items.fileLogInfo.map(o =>
                    <div className="bg-neutral-50 rounded-lg border border-neutral-200 p-4" key={o.fileName}>
                      <div className="flex items-center gap-3 mb-3">
                        <button
                          className="text-red-500 hover:text-red-700 p-2 rounded-lg hover:bg-red-50 transition-colors"
                          title="Delete File"
                          id={o.fileName}
                          onClick={() => deleteFile(o.fileName)}
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                        <button
                          className="text-blue-600 hover:text-blue-800 hover:underline font-medium break-all text-left"
                          title="Download File"
                          type="button"
                          onClick={() => getTempUrl(o.fileName)}
                        >
                          {o.fileName}
                        </button>
                      </div>
                      <div className="pl-10 space-y-1">
                        {o.fileReadInfo.map(info =>
                          <div className="flex items-center gap-2 text-sm text-neutral-600" key={info.loggedTime}>
                            <span>{setAction(info.action)} by {info.user} - {convertToSimpleDateMessage(info.loggedTime)}</span>
                            <GeneralUploadStatus confirm={info.confirmed} action={info.action}/>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                  {items.fileLogInfo.length === 0 && (
                    <p className="text-neutral-500 text-center py-4">No files attached</p>
                  )}
                </div>
              </div>
            </div>

            {/* Notes Section */}
            <AdminApptNotesHandler requestId={data.requestId} />

            {/* Action Buttons */}
            <div className="flex flex-wrap gap-3 mb-4">
              {isPending && (
                <button
                  className="bg-red-500 hover:bg-red-600 text-white font-medium py-2 px-4 rounded-lg transition-colors"
                  type="button"
                  onClick={() => updateRequest(items.appt_requestId, '', 'Cancel', items.appt_firm)}
                >
                  Cancel Appointment
                </button>
              )}
              {isScheduled && (
                <button
                  className="bg-green-600 hover:bg-green-700 text-white font-medium py-2 px-4 rounded-lg transition-colors"
                  type="button"
                  onClick={() => updateRequestCompleted(items.appt_requestId, '', 'Complete', items.appt_firm)}
                >
                  Mark Complete
                </button>
              )}
              <button
                className="bg-neutral-200 hover:bg-neutral-300 text-neutral-700 font-medium py-2 px-4 rounded-lg transition-colors"
                type="button"
                onClick={() => showEditView(items.appt_requestId)}
              >
                Edit Details
              </button>
              <button
                className="bg-neutral-200 hover:bg-neutral-300 text-neutral-700 font-medium py-2 px-4 rounded-lg transition-colors"
                type="button"
                onClick={() => sendInvoice(items.appt_requestId)}
              >
                Generate Invoice
              </button>
            </div>
            <div className="bg-white rounded-xl shadow-md border border-neutral-200 mt-4 mb-3 overflow-hidden">
              <div className="px-3 sm:px-5 py-3 border-b border-neutral-200">
                <h5 className="text-lg font-semibold text-neutral-800">Agent Status</h5>
                <div className="w-12 h-1 bg-blue-500 rounded mt-2"></div>
              </div>
              <div className="p-3 sm:p-5">
                {items.notaryStatus.map((agent, index) => (
                  <AgentCard
                    key={index}
                    agent={agent}
                    formatPhoneNumber={formatPhoneNumber}
                    getProfile={getProfile}
                    updateRequest={updateRequest}
                    apptRequestId={items.appt_requestId}
                    apptFirm={items.appt_firm}
                    apptFilled={items.apptFilled}
                    apptCity={items.apptCity}
                    apptState={items.apptState}
                  />
                ))}
                {items.notaryStatus.length === 0 && (
                  <p className="text-neutral-500 text-center py-4">No agent responses yet</p>
                )}
              </div>
            </div>
          </div>
        ))}</div>)
      }
      <Fragment>
        {editScreen && <AdminEditSelectChoice onMessage={handleMessage} requestDetails={requestDetails} requestId={requestId} searchStatus={data.searchStatus} />}
        {editDetails && <AdminEditCreatedRequest requestId={requestId} searchStatus={data.searchStatus} onEditComplete={() => {
          setEditDetails(false)
          setShowDetails(true)
          // Reload data to get updated values
          const reqData = { data }
          const options = {}
          api
            .postCreate('/get-request-details', options, reqData)
            .then(res => initialLoad(res))
            .catch(err => console.log(err))
        }} />}
        {editNotary && <AdminPickNewNotary notary={requestDetails} requestId={data.requestId} />}
        {assignDirect && <AdminAssignDirect requestId={data.requestId} />}
        {editStatus && <AdminEditStatus requestId={data.requestId} />}
        {showPopup && <PopupOverlay clearModal={clearModal} confirmAction={confirmAction} passedMessage={passedMessage} passedObject={passedObject} />}
        {showNotaryPopup && <ProfilePopupOverlay clearModal={clearModal} confirmAction={confirmAction} email={profileEmail.notaryEmail} />}
      </Fragment>

      {showResponse &&
        <div>
          {updateResponse.map((items =>
            <div>
              {items.message}
            </div>
          ))}
        </div>
      }

    </div>
  )
};

export default AdminViewRequest;